// The cabinet around its pages: the black header (logo, language, who is signed in, sign-out) and, signed in, the
// menu. The Blazor MainLayout and NavMenu - now only on the pages that are the cabinet; the landing page draws its own.

import { isAuthenticated } from './auth.ts';
import { languageSelector } from './components/language-selector.ts';
import { h } from './dom.ts';
import { t } from './i18n.ts';
import type { StateEvent } from './state.ts';
import type { Layout } from './router.ts';
import { on, state } from './state.ts';

interface LayoutHandlers {
    onLanguage: (name: string) => void;
    onLogout: () => void;
}

let root: HTMLElement | null = null;
let main: HTMLElement | null = null;
let handlers: LayoutHandlers | null = null;

const logoutIcon = (): HTMLElement => h('img', { src: '/images/logout_icon.svg', alt: '' });

function header(): HTMLElement {
    const person = state.person;
    const signedIn = isAuthenticated();

    return h('header', { class: 'header' },
        h('div', { class: 'logo' },
            h('img', { src: '/images/logo.svg', alt: 'Lify' }),
            h('div', { class: 'divider' }),
            h('span', { class: 'title' }, 'Referral')),
        h('div', { class: 'spacer' }),
        languageSelector({
            value: state.locale,
            locales: state.locales,
            label: t('influencer.web.layout.header.language.placeholder'),
            onChange: (name: string) => handlers?.onLanguage(name)
        }),
        signedIn && h('div', { class: 'personal-info' },
            person && [
                h('div', { class: 'initials' },
                    h('span', null, `${(person.name || '').slice(0, 1)}${(person.surname || '').slice(0, 1)}`)),
                h('div', { class: 'name-email' },
                    h('span', { class: 'name' }, [person.name, person.surname].filter(Boolean).join(' ')),
                    h('span', { class: 'email' }, person.email || '')),
                h('button', { type: 'button', class: 'btn log-out-button', onclick: () => handlers?.onLogout() },
                    t('influencer.web.layout.header.logoutButton.text'), logoutIcon()),
                h('button', {
                    type: 'button',
                    class: 'btn log-out-button-mobile',
                    'aria-label': t('influencer.web.layout.header.logoutButton.text'),
                    onclick: () => handlers?.onLogout()
                }, logoutIcon())
            ]));
}

function navLink(href: string, icon: string, key: string, exact: boolean): HTMLElement {
    const path = location.pathname.toLowerCase();
    const active = exact ? path === href : path.startsWith(href);

    return h('div', { class: 'nav-item' },
        h('a', { class: 'nav-link' + (active ? ' active' : ''), href, 'aria-current': active ? 'page' : null },
            h('img', { src: icon, alt: '' }),
            t(key)));
}

function sidebar(): HTMLElement {
    return h('aside', { class: 'sidebar' },
        h('nav', { class: 'nav' },
            navLink('/dashboard', '/images/dashboard_icon.svg', 'influencer.web.layout.navMenu.buttons.dashboard.text', true),
            navLink('/withdraw-history', '/images/withdraw_history_icon.svg', 'influencer.web.layout.navMenu.buttons.withdrawHistory.text', false)));
}

/** Draws the header and the menu again; the page inside is left as it is. */
export function refreshLayout(): void {
    if (!root) return;

    const layout = root.querySelector('.layout');

    if (!layout) return;

    layout.querySelector('header')?.replaceWith(header());
    layout.querySelector('aside')?.remove();

    if (isAuthenticated()) layout.insertBefore(sidebar(), main);
}

/** The element the page draws into: the cabinet's main area, or the bare app root for a page with no layout. */
export function showLayout(layout: Layout): HTMLElement {
    const app = document.getElementById('app')!;

    if (layout === 'none') {
        root = null;
        main = null;

        const view = h('div', { class: 'bare' });

        app.replaceChildren(view);

        return view;
    }

    if (!root || !main) {
        main = h('main', { class: 'content' });
        root = h('div', { class: 'cabinet' }, h('div', { class: 'layout' }, header(), main));
        app.replaceChildren(root);
    }

    const area = main;

    area.replaceChildren();
    refreshLayout();

    return area;
}

export function initLayout(layoutHandlers: LayoutHandlers): void {
    handlers = layoutHandlers;

    for (const name of ['languageChanged', 'localesChanged', 'personChanged', 'authChanged'] as const satisfies readonly StateEvent[]) {
        on(name, refreshLayout);
    }
}
